/**
 * ColumnAlterFormBody — state-machine + commit body for `alter-column`. Renders
 * `ColumnFormFields` plus a Cancel/Apply footer; the Apply handler runs the
 * `commitAlterColumn` pipeline. Engine-aware rules live here:
 *   - sqlite: caller should not render this (the trigger surface gates with
 *     `isAlterColumnSupported`); included as a defensive runtime guard.
 *   - mssql / mysql: type field is required even when only nullability /
 *     default change. The body falls back to `currentType` when the user
 *     leaves the field unchanged but `requiresFullTypeReclaration(engine)`
 *     is true, matching the DSE2 §3.A1 per-engine acceptance notes.
 *   - postgres: identity-restart UI is enabled (DSE2 §3.A2 — RESTART WITH n).
 *     Other engines see the section disabled with the per-engine redirect
 *     tooltip.
 *
 * One immediate caller: `ColumnFormPopover`, mounted by `ColumnsTab`'s per-row
 * Edit affordance. BOTH surfaces reach it through that one route — the Table
 * Designer mounts `TableEditor` directly, and the compact DDL editor mounts the
 * same `TableEditor` via `CompactDdlShell` — which is why threading
 * `columnNameOrigin` from the clicked row covers every way a user can open this
 * form. The prop stays optional for a caller with no occurrence to name;
 * nothing in the app is in that position today.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice A1, § Slice A2.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { isAmbiguousColumnNameError } from '../../../../sql-resolver/ddl/apply/columnEdit.alterTable';
import type { ExistingNameOrigin } from '../../../../sql-resolver/ddl/apply/dialect.quoting';
import type { ColumnChange } from '../../../../sql-resolver/types/ddlEdit';
import {
  commitAlterColumn,
  isNonEmptyColumnChange,
  requiresFullTypeReclaration,
} from '../../edit/edits/alterColumn';
import {
  getIdentityRestartUnsupportedTooltip,
  isIdentityRestartSupported,
} from '../../edit/edits/setColumnIdentity';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { ambiguousColumnMessage } from './ambiguousColumnCopy';
import {
  ColumnFormFields,
  type ColumnFormDefaultMode,
  type ColumnFormIdentityMode,
} from './ColumnFormFields';

export interface ColumnAlterFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly columnName: string;
  /** WHICH occurrence `columnName` names, when the caller knows — the Columns
   *  tab's per-row Edit passes the clicked row's own `nameOrigin`. Omitted by
   *  a caller holding only a name; the source statement then answers, and
   *  refuses when it declares that name both delimited and bare. */
  readonly columnNameOrigin?: ExistingNameOrigin | null;
  readonly currentType: string | null;
  readonly currentNullable: boolean | null;
  readonly currentDefault: string | null;
  readonly testId: string;
  /** Fires after a successful commit; caller closes the surrounding popover. */
  readonly onCommitted?: () => void;
  /** Fires when Cancel is clicked. */
  readonly onCancel?: () => void;
}

export function ColumnAlterFormBody(props: ColumnAlterFormBodyProps) {
  const { editContext, columnName, columnNameOrigin, currentType, currentNullable, currentDefault, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const requireType = requiresFullTypeReclaration(engine);

  const identityRestartSupported = isIdentityRestartSupported(engine);
  const identityUnsupportedTooltip = getIdentityRestartUnsupportedTooltip(engine);

  const [type, setType] = useState<string>(requireType ? currentType ?? '' : '');
  const [nullableMode, setNullableMode] = useState<'unchanged' | 'null' | 'not-null'>('unchanged');
  const [defaultMode, setDefaultMode] = useState<ColumnFormDefaultMode>('unchanged');
  const [defaultExpr, setDefaultExpr] = useState<string>('');
  const [identityMode, setIdentityMode] = useState<ColumnFormIdentityMode>('unchanged');
  const [identityStart, setIdentityStart] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  /** The thrown error's own text, kept when the box shows a rewritten
   *  sentence — a developer reading the popover still gets the precise one. */
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildChange(): ColumnChange | { error: string } | null {
    const draft: {
      type?: string;
      nullable?: boolean;
      default?: ColumnChange['default'];
      identity?: ColumnChange['identity'];
    } = {};
    const typeChanged = type.trim() !== '' && type.trim() !== (currentType ?? '');
    if (typeChanged) draft.type = type.trim();
    if (nullableMode === 'null') draft.nullable = true;
    if (nullableMode === 'not-null') draft.nullable = false;
    if (defaultMode === 'drop') {
      draft.default = { kind: 'drop' };
    } else if (defaultMode === 'set') {
      const expr = defaultExpr.trim();
      if (expr === '') return null;
      draft.default = { kind: 'set', expr };
    }
    if (identityMode === 'restart') {
      const trimmed = identityStart.trim();
      const start = Number(trimmed);
      if (trimmed === '' || !Number.isFinite(start) || !Number.isInteger(start)) {
        return { error: 'Provide an integer start value to restart the identity sequence.' };
      }
      draft.identity = { kind: 'restart', start };
    }
    if (!isNonEmptyColumnChange(draft)) return null;
    if (requireType && draft.type === undefined && (draft.nullable !== undefined || draft.default !== undefined)) {
      const fallback = currentType ?? '';
      if (fallback === '') return null;
      draft.type = fallback;
    }
    return draft as ColumnChange;
  }

  async function handleSubmit() {
    setError(null);
    setErrorDetail(null);
    const built = buildChange();
    if (!built) {
      setError('Provide at least one field to change.');
      return;
    }
    if ('error' in built) {
      setError(built.error);
      return;
    }
    setPending(true);
    try {
      await commitAlterColumn(editContext, {
        columnName,
        columnNameOrigin: columnNameOrigin ?? undefined,
        change: built,
      });
      onCommitted?.();
    } catch (err) {
      if (isAmbiguousColumnNameError(err)) {
        setError(ambiguousColumnMessage(columnName, engine, 'edit-as-sql'));
        setErrorDetail(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setPending(false);
    }
  }

  return (
    <div data-testid={testId}>
      <ColumnFormFields
        columnName={columnName}
        requireType={requireType}
        engine={engine}
        currentType={currentType}
        currentNullable={currentNullable}
        currentDefault={currentDefault}
        type={type}
        setType={setType}
        nullableMode={nullableMode}
        setNullableMode={setNullableMode}
        defaultMode={defaultMode}
        setDefaultMode={setDefaultMode}
        defaultExpr={defaultExpr}
        setDefaultExpr={setDefaultExpr}
        identityMode={identityMode}
        setIdentityMode={setIdentityMode}
        identityStart={identityStart}
        setIdentityStart={setIdentityStart}
        identityRestartSupported={identityRestartSupported}
        identityUnsupportedTooltip={identityUnsupportedTooltip}
        testId={testId}
      />
      {error ? (
        <div
          className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
          title={errorDetail ?? undefined}
        >
          {error}
        </div>
      ) : null}
      <div className="mt-4 flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? 'Applying…' : 'Apply'}
        </Button>
      </div>
    </div>
  );
}
